import './About.css';
import { useEffect, useRef, useState } from 'react';

const STATS = [
  { num: '2+',  label: 'Years Experience' },
  { num: '20+', label: 'Projects' },
  { num: '98%', label: 'Client Satisfaction' },
  { num: '5+',  label: 'Tools Mastered'    },
];

const SKILLS = [
  { category: 'Design',      items: ['Figma', 'Prototyping', 'Motion Design', 'Design Systems'] },
  { category: 'Research',    items: ['User Interviews', 'Usability Testing', 'Journey Mapping', 'A/B Testing'] },
  { category: 'Engineering', items: ['Angular', 'TypeScript', 'CSS / Animation', 'Accessibility'] },
];

const TIMELINE = [
  { year: '2024', role: 'UI/UX Engineer Intern',      org: 'Epic Lanka Technologies'   },
  {  year: '2024', role: 'UI/UX Engineer Intern',      org: 'Softmint Software Solutions'},

];

function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function About() {
  const [mousePos, setMousePos]       = useState({ x: 0, y: 0 });
  const [cursorBig, setCursorBig]     = useState(false);
  const [activeSkill, setActiveSkill] = useState(null);
  const [heroRef, heroVisible]        = useReveal();
  const [statsRef, statsVisible]      = useReveal();
  const [skillsRef, skillsVisible]    = useReveal();
  const [timelineRef, timelineVisible]= useReveal();

  useEffect(() => {
    const move = (e) => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('mousemove', move);
    return () => window.removeEventListener('mousemove', move);
  }, []);

  const hoverOn  = () => setCursorBig(true);
  const hoverOff = () => setCursorBig(false);

  const imgParallax = {
    transform: `translate(
      ${(mousePos.x / window.innerWidth  - 0.5) * -10}px,
      ${(mousePos.y / window.innerHeight - 0.5) * -7}px
    )`,
    transition: 'transform 0.18s ease-out',
  };

  return (
    <div className="ab-root">
      {/* Cursor */}
      <div
        className={`ab-cursor ${cursorBig ? 'ab-cursor--big' : ''}`}
        style={{ left: mousePos.x, top: mousePos.y }}
      />

      {/* ── HERO ── */}
      <section
        className={`ab-hero ${heroVisible ? 'ab-hero--in' : ''}`}
        ref={heroRef}
      >
        {/* Image column */}
        <div className="ab-hero__img-col">
          <div className="ab-hero__img-frame" style={imgParallax}>
            {/* Texture overlay */}
            <div className="ab-hero__img-texture" aria-hidden="true" />
            <img src="/tiger3.jpg" alt="Yelani" className="ab-hero__img" />
          </div>
          {/* Decorative ring */}
          <div className="ab-hero__ring" aria-hidden="true" />
          {/* Vertical tag */}
          <span className="ab-hero__vert-tag" aria-hidden="true">UI / UX Engineer</span>
        </div>

        {/* Text column */}
        <div className="ab-hero__text">
          <p className="ab-hero__eyebrow">About Me</p>

          <h1 className="ab-hero__heading">
            Design that<br />
            <em>thinks</em> as well<br />
            as it looks.
          </h1>

          <p className="ab-hero__body">
I'm a UI/UX Engineer with two years of hands-on experience, passionate about building interfaces that feel effortless to use. I work at the intersection of user psychology, visual craft, and front-end engineering — turning real user needs into clean, purposeful design.
          </p>
          {/* <p className="ab-hero__body">
            With 7+ years leading design at growth-stage companies, I've built systems, 
            shipped products, and mentored teams that collectively serve millions of users. 
            Every decision I make is rooted in empathy — for the user, and for the developer 
            who has to build it.
          </p> */}

          <a href="/work" className="ab-hero__cta" onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
            See My Work <span className="ab-hero__cta-arrow">↗</span>
          </a>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <div
        className={`ab-stats ${statsVisible ? 'ab-stats--in' : ''}`}
        ref={statsRef}
      >
        {STATS.map(({ num, label }, i) => (
          <div
            key={label}
            className="ab-stats__item"
            style={{ transitionDelay: `${i * 0.08}s` }}
          >
            <span className="ab-stats__num">{num}</span>
            <span className="ab-stats__label">{label}</span>
          </div>
        ))}
      </div>

      {/* ── SKILLS ── */}
      <section
        className={`ab-skills ${skillsVisible ? 'ab-skills--in' : ''}`}
        ref={skillsRef}
      >
        <p className="ab-section-label">Expertise</p>
        <div className="ab-skills__grid">
          {SKILLS.map(({ category, items }, ci) => (
            <div
              key={category}
              className="ab-skills__col"
              style={{ transitionDelay: `${ci * 0.12}s` }}
            >
              <h3 className="ab-skills__category">{category}</h3>
              <ul className="ab-skills__list">
                {items.map((item) => (
                  <li
                    key={item}
                    className={`ab-skills__item ${activeSkill === item ? 'ab-skills__item--active' : ''}`}
                    onMouseEnter={() => { setActiveSkill(item); hoverOn(); }}
                    onMouseLeave={() => { setActiveSkill(null); hoverOff(); }}
                  >
                    <span className="ab-skills__bullet" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ── TIMELINE ── */}
      <section
        className={`ab-timeline ${timelineVisible ? 'ab-timeline--in' : ''}`}
        ref={timelineRef}
      >
        <p className="ab-section-label">Career</p>
        <div className="ab-timeline__track">
          {TIMELINE.map(({ year, role, org }, i) => (
            <div
              key={i}
              className="ab-timeline__row"
              style={{ transitionDelay: `${i * 0.1}s` }}
            >
              <span className="ab-timeline__year">{year}</span>
              <div className="ab-timeline__line-wrap" aria-hidden="true">
                <div className="ab-timeline__dot" />
                {i < TIMELINE.length - 1 && <div className="ab-timeline__connector" />}
              </div>
              <div className="ab-timeline__info">
                <span className="ab-timeline__role">{role}</span>
                <span className="ab-timeline__org">{org}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FOOTER STRIP ── */}
      <div className="ab-footer-strip">
        <span className="ab-footer-strip__text">
          Open to new opportunities &amp; collaborations
        </span>
        <a
          href="mailto:hello@yelani.design"
          className="ab-footer-strip__email"
          onMouseEnter={hoverOn}
          onMouseLeave={hoverOff}
        >
          hello@yelani.design ↗
        </a>
      </div>
    </div>
  );
}

export default About;
